import { useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)", line: "var(--pr-line)", gold: "var(--pr-gold)" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const nextOccurrence = (dayIdx, todayIso) => {
  const [y, m, d] = todayIso.split("-").map(Number);
  const t = new Date(y, m - 1, d);
  const target = (dayIdx + 1) % 7; // JS: 0=Sun..6=Sat
  const diff = (target - t.getDay() + 7) % 7;
  t.setDate(t.getDate() + diff);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

export const SetupBasicsCard = ({ today, onSaved }) => {
  const [dayIdx, setDayIdx] = useState(() => {
    const [y, m, d] = today.split("-").map(Number);
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
  });
  const [startDate, setStartDate] = useState(() => today);
  const [delay, setDelay] = useState("7");
  const [workDays, setWorkDays] = useState(5);
  const [saving, setSaving] = useState(false);

  const pickDay = (i) => {
    setDayIdx(i);
    setStartDate(nextOccurrence(i, today));
  };
  const onDate = (v) => {
    setStartDate(v);
    if (v) {
      const [y, m, d] = v.split("-").map(Number);
      setDayIdx((new Date(y, m - 1, d).getDay() + 6) % 7);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!startDate || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/caddy/setup-basics`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start_date: startDate, payday_delay: parseInt(delay || "7", 10), days_per_week: workDays }),
      });
      const data = await res.json();
      if (!res.ok) {
        const dd = data.detail;
        toast.error(typeof dd === "string" ? dd : dd?.message || "Couldn't save, try again");
        return;
      }
      toast.success("Your race calendar is set 🏁");
      onSaved(data);
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="rounded-2xl p-5 lg:p-6 pr-card"
      data-testid="setup-basics-card"
    >
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>Set up your race calendar</p>
      <p className="text-sm font-bold mb-4" style={{ color: COLOR.ink }}>Your race, your rhythm, three quick picks.</p>

      <p className="text-xs font-semibold mb-2" style={{ color: COLOR.ink2 }}>When does your work week start?</p>
      <div className="grid grid-cols-7 gap-1.5 mb-4">
        {DAYS.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => pickDay(i)}
            className="py-2 rounded-lg text-[11px] font-bold border transition-colors"
            style={
              i === dayIdx
                ? { background: "var(--pr-ink-surface)", color: "#fff", borderColor: COLOR.ink }
                : { background: "var(--pr-card)", color: COLOR.ink2, borderColor: COLOR.line }
            }
            data-testid={`setup-day-${d.toLowerCase()}`}
          >
            {d}
          </button>
        ))}
      </div>

      <p className="text-xs font-semibold mb-1.5" style={{ color: COLOR.ink2 }}>
        When should your first relay begin? <span style={{ color: COLOR.muted }}>(a {DAY_FULL[dayIdx]})</span>
      </p>
      <input
        type="date"
        value={startDate}
        min={today}
        onChange={(e) => onDate(e.target.value)}
        className="w-full px-3.5 py-3 rounded-xl pr-input text-sm font-semibold mb-4"
        style={{ color: COLOR.ink }}
        data-testid="setup-start-date"
      />

      <p className="text-xs font-semibold mb-1.5" style={{ color: COLOR.ink2 }}>
        How many days a week do you want to work?
      </p>
      <div className="grid grid-cols-7 gap-1 mb-3">
        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setWorkDays(n)}
            className="py-2 rounded-lg text-[11px] font-bold border transition-colors"
            style={
              n === workDays
                ? { background: "var(--pr-ink-surface)", color: "#fff", borderColor: COLOR.ink }
                : { background: "var(--pr-card)", color: COLOR.ink2, borderColor: COLOR.line }
            }
            data-testid={`setup-workdays-${n}`}
          >
            {n}
          </button>
        ))}
      </div>

      <p className="text-xs font-semibold mb-1.5" style={{ color: COLOR.ink2 }}>
        How many days after a week ends does your money usually land?
      </p>
      <div className="flex items-center gap-2 mb-1">
        <input
          type="number"
          inputMode="numeric"
          min="0"
          max="60"
          value={delay}
          onChange={(e) => setDelay(e.target.value)}
          className="w-20 px-3 py-3 rounded-xl pr-input text-sm font-bold text-center"
          style={{ color: COLOR.ink }}
          data-testid="setup-payday-delay"
        />
        <span className="text-xs" style={{ color: COLOR.ink2 }}>days, client invoices often run 3–14</span>
      </div>
      <p className="text-[11px] leading-relaxed mb-3" style={{ color: COLOR.muted }}>
        Golden Rule: once your race starts, your start date and payday rhythm stay put.
      </p>

      <button
        type="submit"
        disabled={saving || !startDate}
        className="w-full py-3 rounded-full font-bold text-sm disabled:opacity-40 pr-btn-primary"
        data-testid="setup-basics-save"
      >
        {saving ? "Setting up…" : "Lock in my calendar 🏁"}
      </button>
    </form>
  );
};
